import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { groupBy, isEqual } from 'lodash'
import { Box } from '@chatterbug/aaron'
import { usePageParams } from 'src/utils/params'
import { get } from 'lib/http'
import routes from 'src/lib/rails-routes/rails-routes'
import List from 'src/ui/List/List'
import MyCourseListItem from 'src/pages/MyCourse/subcomponents/MyCourseUnitListItem/MyCourseUnitListItem'
import UnitListItemDropPlaceholder from 'src/pages/MyCourse/subcomponents/UnitListItemDropPlaceholder/UnitListItemDropPlaceholder'
import Empty from 'src/pages/MyCourse/subcomponents/Empty/Empty'
import C1Prompt from 'src/pages/MyCourse/subcomponents/C1Prompt/C1Prompt'

const GROUP_FIRST = 'first'
const GROUP_LAST = 'last'

const getGroupedUnits = (units) => {
  let beforeCurrent = true

  return groupBy(units, (unit) => {
    if (unit?.progress?.isCurrent) {
      beforeCurrent = false
    }

    return beforeCurrent ? GROUP_FIRST : GROUP_LAST
  })
}

const MyCourseUnitList = ({
  isEditMode,
  units = [],
  onReorder,
  onRemove,
  currentCefrLevel,
}) => {
  const { l2Code } = usePageParams()
  const currentUnitContainerRef = useRef(null)
  const isScrolled = useRef(false)

  const [unitsBeforeGrouping, setUnitsBeforeGrouping] = useState(units)
  const [groupedUnits, setGroupedUnits] = useState(getGroupedUnits(units))

  useEffect(() => {
    if (!isEqual(units, unitsBeforeGrouping)) {
      setUnitsBeforeGrouping(units)
      setGroupedUnits(getGroupedUnits(units))
    }
  }, [units, unitsBeforeGrouping, setUnitsBeforeGrouping, setGroupedUnits])

  useEffect(() => {
    if (!isScrolled.current && groupedUnits[GROUP_FIRST]?.length > 0) {
      isScrolled.current = true // scroll only once, when user opens a page with some completed items on it
      currentUnitContainerRef.current?.scrollIntoView?.({ behavior: 'smooth' })
    }
  }, [groupedUnits])

  const commonListProps = {
    getItemUrl: (item) => routes.advanced_unit(l2Code, item.id),
    itemIconShape: 'rounded',
    ItemComponent: MyCourseListItem,
    onReorder,
    onRemove,
    DropPlaceholderComponent: UnitListItemDropPlaceholder,
  }

  if (units.length === 0 && currentCefrLevel?.toLowerCase() === 'c1') {
    return (
      <Box mt={{ _: '3x', laptop: '1x' }}>
        <Empty />
      </Box>
    )
  }

  if (units.length === 0 && currentCefrLevel?.toLowerCase() < 'c1') {
    return (
      <Box mt={{ _: '3x', laptop: '1x' }}>
        <C1Prompt
          l2Code={l2Code}
          onClick={async () => {
            await get(routes.unlock_c_level(l2Code))
            window.location = routes.advanced_home(l2Code)
          }}
        />
      </Box>
    )
  }

  return (
    <>
      {groupedUnits[GROUP_FIRST]?.length > 0 && (
        <List items={groupedUnits[GROUP_FIRST]} {...commonListProps} />
      )}
      {groupedUnits[GROUP_LAST]?.length > 0 && (
        <Box sx={{ position: 'relative' }}>
          <Box
            ref={currentUnitContainerRef}
            sx={{ position: 'absolute', top: -110 }} // value depends on MyCourse header height during scrolling
          />
          <List
            items={groupedUnits[GROUP_LAST]}
            draggable={isEditMode}
            removable={isEditMode}
            {...commonListProps}
          />
        </Box>
      )}
    </>
  )
}

MyCourseUnitList.propTypes = {
  isEditMode: PropTypes.bool,
  units: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
    })
  ),
  onReorder: PropTypes.func,
  onRemove: PropTypes.func,
  currentCefrLevel: PropTypes.string.isRequired,
}

export default MyCourseUnitList
